import React from 'react';
import { Box, Typography } from '@mui/material';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import PathTabs from './PathTabs';
import ScriptBubble from './ScriptBubble';

// 帶入欄位的中文標籤（用於底部插入列）
const SLOT_INSERT_LABELS = {
  name: '姓名', date: '日期', time: '時間', phone: '電話',
  adults: '大人人數', children: '小孩人數', address: '地址',
  issue_description: '問題描述', duck: '烤鴨', kid_chairs: '兒童椅',
  // 外撥提醒回應 + 修改訂位新值
  reminder_response: '備註資訊',
  new_date: '新日期', new_time: '新時間', new_adults: '新人數',
  new_phone: '新電話', new_name: '新姓名',
  party_count: '總人數',
};

const ConversationScript = ({
  conversationPaths,
  activePathId,
  onPathChange,
  onTextChange,
  onPlayAudio,
  onStopAudio,
  regeneratingNodes,
  onRegenerateNode,
  slots,
  readOnly = false,
}) => {
  if (!conversationPaths || conversationPaths.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">尚無對話腳本資料</Typography>
      </Box>
    );
  }

  const activePath = conversationPaths.find(p => p.id === activePathId)
    || conversationPaths[0];

  // 在 collect_slot 後插入客戶回應指示
  const stepsWithCustomerResponse = [];
  activePath.steps.forEach((step) => {
    // branch_divider 和 customer_response 直接透傳
    stepsWithCustomerResponse.push(step);
    // collect_slot 後加一個客戶回應提示
    if (step.type === 'collect_slot' && step.isUserFacingSlot) {
      stepsWithCustomerResponse.push({
        _customerResponse: true,
        nodeId: `${step.nodeId}_response`,
        slotName: step.slotName,
        // 使用後端的豐富描述（decision slot 或 data slot）
        responseText: step.customerResponseHint || null,
      });
    }
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* 路徑分頁 */}
      <PathTabs
        paths={conversationPaths}
        activePathId={activePath.id}
        onPathChange={onPathChange}
      />

      {/* 對話氣泡區 */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 2.5,
          py: 2,
          minHeight: 0,
          bgcolor: '#f8f9fa',
        }}
      >
        {stepsWithCustomerResponse.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
            此路徑無可編輯的對話步驟
          </Typography>
        ) : (
          stepsWithCustomerResponse.map((item, idx) => {
            // 分支分隔線（橘色 alt_route）
            if (item.type === 'branch_divider') {
              return (
                <Box
                  key={item.nodeId}
                  sx={{
                    my: 2, mx: 1, px: 2, py: 1,
                    borderRadius: '8px',
                    border: '1px solid #ffeeba',
                    bgcolor: '#fff8f0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: 0.5, color: '#e67e22', fontSize: '13px', fontWeight: 500,
                  }}
                >
                  <AltRouteIcon sx={{ fontSize: 16 }} />
                  {item.title}
                </Box>
              );
            }

            // 客戶回應指示（來自後端 customer_response 或前端插入的 _customerResponse）
            if (item._customerResponse || item.type === 'customer_response') {
              const slotLabel = SLOT_INSERT_LABELS[item.slotName] || item.slotName;
              const responseText = item.responseText || item.text
                || `客戶回答${slotLabel}`;

              return (
                <Box
                  key={item.nodeId}
                  sx={{
                    display: 'flex', flexDirection: 'row-reverse',
                    gap: 1, mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '15px', bgcolor: '#f0e6ff',
                    }}
                  >
                    👤
                  </Box>
                  <Box sx={{ maxWidth: '72%' }}>
                    <Box
                      sx={{
                        px: 2, py: 1,
                        bgcolor: '#f0e6ff', border: '1px solid #ddd0f0',
                        borderRadius: '12px', borderTopRightRadius: '4px',
                        fontStyle: 'italic', opacity: 0.7, fontSize: '14px',
                      }}
                    >
                      {responseText}
                    </Box>
                  </Box>
                </Box>
              );
            }

            const field = item.type === 'collect_slot' ? 'prompt_text'
              : item.type === 'api_call' ? 'pre_play_text' : 'text';
            const regenKey = `${item.nodeId}:${field}`;

            return (
              <ScriptBubble
                key={`${item.nodeId}-${idx}`}
                step={item}
                onTextChange={onTextChange}
                onPlayAudio={onPlayAudio}
                onStopAudio={onStopAudio}
                isRegenerating={regeneratingNodes?.has(regenKey)}
                onRegenerateAudio={onRegenerateNode}
                readOnly={readOnly}
              />
            );
          })
        )}
      </Box>

    </Box>
  );
};

export default ConversationScript;
